import {
  isBoolean,
  isDate,
  isEmpty,
  get,
  set,
  last,
  each,
  extend,
  indexOf,
  cloneDeep,
  isObject,
  keys,
  isString,
  isNumber,
  chain,
} from 'lodash-es';
import axios from 'axios';
import debug from 'debug';
import type { Method, AxiosRequestConfig } from 'axios';
import { OpenAPIV3 } from 'openapi-types';
import { enhanceResponseWithEtag, formatPayload } from './helpers';
import {
  RequestArgs,
  OpenapiOperation,
  OpenapiConnectionSpec,
  Connection,
} from './types';
import * as OT from '../openapi-tools';

const log = debug('itp:core:connections:openapi');

const REQUEST_TIMEOUT_MS = 20000; // 20 sec

function isEmptyValue(value: any) {
  if (value == null) {
    return true;
  }
  if (isBoolean(value) || isNumber(value) || isDate(value)) {
    return false;
  }
  return isEmpty(value);
}

function getReference<T = any>(doc: OpenAPIV3.Document, path: string) {
  if (!path.startsWith('#')) {
    throw new Error(
      `Invalid $ref path. It must start with "#" character. path=${path}`,
    );
  }

  let result = get(doc, path.split('/').slice(1));

  if (!result) {
    throw new Error(`$ref not found: ${path}`);
  }

  return result as T;
}

function valueAs<T>(doc: OpenAPIV3.Document, data: T | object): T {
  if (get(data, '$ref', null) !== null) {
    return getReference<T>(doc, (data as OpenAPIV3.ReferenceObject).$ref);
  }

  return data as T;
}

export function getOperationNEXT(
  doc: OpenAPIV3.Document,
  operationId: string,
): OpenAPIV3.OperationObject {
  if (operationId.indexOf('/') > -1) {
    const lastToken = last(operationId.split('/'));
    if (lastToken) {
      operationId = lastToken;
    }
  }

  let result: OpenAPIV3.OperationObject | null = null;

  each(doc.paths, (pathItem, url) => {
    each(pathItem, (op, method) => {
      if (get(op, 'operationId') === operationId) {
        result = op as OpenAPIV3.OperationObject;
      }
    });
  });

  if (!result) {
    throw new Error(`invalid OpenAPI operationId: ${operationId}`);
  }

  return result;
}

function getOperation(doc: object, id: string): OpenapiOperation {
  if (id.startsWith('http://') || id.startsWith('https://')) {
    return {
      url: id,
      method: 'get',
      responses: {},
      operationId: 'direct_url',
    } as any;
  }

  let result: OpenapiOperation | null = null;

  if (id.indexOf('/') > -1) {
    const lastToken = last(id.split('/'));
    if (lastToken) {
      id = lastToken;
    }
  }

  const paths: Dictionary<any> = get(doc, 'paths', {});
  each(paths, (v, url) =>
    each(v, (opts, method) => {
      if (opts.operationId === id) {
        result = extend({ url, method }, opts);
        return false;
      }
    }),
  );

  if (!result) {
    log(doc);
    throw new Error(`OpenAPI operation not found: ${id}.`);
  }

  return result;
}

export function hasProperty(
  name: string,
  schema: OpenAPIV3.SchemaObject,
): boolean {
  return !!(schema.properties && name in schema.properties);
}

function getProperty(
  name: string,
  schema: OpenAPIV3.SchemaObject,
  doc: OpenAPIV3.Document,
): OpenAPIV3.SchemaObject {
  let result;

  if (schema.properties && name in schema.properties) {
    result = valueAs<OpenAPIV3.SchemaObject>(doc, schema.properties[name]);
  }

  if (!result) {
    throw new Error(
      `OpenAPI property not found: ${name}. (schema=${schema.title || schema.description})`,
    );
  }

  return result;
}

export function getPropertyRules(
  schema: string,
  property: string,
  doc: OpenAPIV3.Document,
) {
  const sch = OT.getSchema(doc, schema);

  if (!hasProperty(property, sch)) {
    return { readonly: true };
  }

  const prop = getProperty(property, sch, doc),
    result: Dictionary<any> = {};

  if (prop.maximum != null) {
    result['max_value'] = prop.maximum;
  }

  if (prop.minimum != null) {
    result['min_value'] = prop.minimum;
  }

  if (prop.maxLength != null) {
    result['max'] = prop.maxLength;
  }

  if (prop.minLength != null) {
    result['min'] = prop.minLength;
  }

  if (indexOf(sch.required, property) > -1) {
    result['required'] = true;
  }

  if (prop.enum) {
    result['oneOf'] = cloneDeep(prop.enum);
  }

  return result;
}

function getContentType(op: OpenapiOperation): string | undefined {
  if (op.requestBody) {
    const content = get(op.requestBody, 'content');
    if (isObject(content)) {
      return keys(content)[0];
    }
  }
}

export interface OpenapiSchema extends OpenAPIV3.BaseSchemaObject {
  doc: OpenAPIV3.Document;
}

export class OpenapiSchema implements OpenapiSchema {
  doc: OpenAPIV3.Document;

  constructor(schema: OpenAPIV3.SchemaObject, doc: OpenAPIV3.Document) {
    Object.assign(this, schema);
    this.doc = doc;
  }

  hasProperty(name: string) {
    return !!(this.properties && name in this.properties);
  }

  getProperty(name: string) {
    return getProperty(name, this, this.doc);
  }
}

export class Openapi implements Connection {
  readonly id: string;
  readonly config: OpenapiConnectionSpec;
  readonly doc: OpenAPIV3.Document;
  readonly acceptLanguage?: string;

  constructor(doc: object, spec: OpenapiConnectionSpec, language?: string) {
    this.id = spec.name;
    this.doc = doc as OpenAPIV3.Document;
    this.config = spec;
    this.acceptLanguage = language;
  }

  static load(spec: OpenapiConnectionSpec, language: string): Promise<Openapi> {
    if (spec.schema) {
      log(`create "${spec.name}" connection from offline schema`);
      return Promise.resolve(new Openapi(spec.schema, spec, language));
    }

    log(`[${spec.name}] loading ${spec.url}`);

    return axios
      .get(spec.url, { timeout: REQUEST_TIMEOUT_MS })
      .then((resp) => new Openapi(resp.data, spec, language));
  }

  getPropertyRules(schema: string, property: string): object {
    return getPropertyRules(schema, property, this.doc);
  }

  getOpenapiSchema(name: string) {
    return new OpenapiSchema(OT.getSchema(this.doc, name), this.doc);
  }

  getSchema(name: string) {
    return OT.getSchema(this.doc, name);
  }

  getSchemaDefaults(name: string) {
    return OT.getSchemaDefaults(this.doc, OT.getSchema(this.doc, name));
  }

  getOperationResponseSchema(
    operationId: string,
    response: string = '200',
    content: string = 'application/json',
  ) {
    const op = getOperationNEXT(this.doc, operationId),
      resp = valueAs<OpenAPIV3.ResponseObject>(
        this.doc,
        op.responses[response],
      );

    let schema = resp.content ? resp.content[content].schema : null;

    if (!schema) {
      throw new TypeError(`Cannot resolve for ${op.operationId}`);
    }

    return valueAs<OpenAPIV3.SchemaObject>(this.doc, schema);
  }

  getOperation(name: string) {
    return getOperation(this.doc, name);
  }

  urlFor(op: OpenapiOperation | string, args?: object, content?: any) {
    const operation = isString(op) ? this.getOperation(op) : op;
    const paramNamesUsed: string[] = [];

    let result = operation.url.startsWith('http')
      ? operation.url
      : `${this.config.baseUrl}${operation.url}`;

    function getvalue(name: string, required: boolean = true) {
      let value = args ? get(args, name) : null;

      if (value == null && content) {
        value = get(content, name);
      }

      if (required && isEmptyValue(value)) {
        throw new Error(`Parameter value required for "${name}".`);
      }

      if (isString(value)) {
        return encodeURIComponent(value);
      }

      return value;
    }

    chain(operation.parameters)
      .filter({ in: 'path' })
      .each((p) => {
        let value = getvalue(p.name, p.required);
        if (!isEmptyValue(value)) {
          result = result.replace(`{${p.name}}`, value);
          paramNamesUsed.push(p.name);
        }
      })
      .value();

    // log('urlFor [0] %s', result);

    // further processing the url string in case of errorenous apidoc...
    const matches = result.match(/\{([\w-]+)\}/g);

    if (matches) {
      each(
        matches,
        (match) =>
          (result = result.replace(match, getvalue(match.slice(1, -1), true))),
      );
    }

    const queryParameters: string[] = [];

    // append query params
    chain(operation.parameters)
      .filter({ in: 'query' })
      .each((p) => {
        let value = getvalue(p.name, p.required);
        if (value != null) {
          queryParameters.push(`${p.name}=${value}`);
          paramNamesUsed.push(p.name);
        }
      })
      .value();

    if (args) {
      for (let [name, value] of Object.entries(args)) {
        if (paramNamesUsed.indexOf(name) > -1) {
          continue;
        }
        if (value != null) {
          queryParameters.push(`${name}=${value}`);
        }
      }
    }

    if (queryParameters.length) {
      result += '?' + queryParameters.join('&');
    }

    log('urlFor [%o] result is %s', op, result);

    return result;
  }

  headersFor(op: OpenapiOperation, args?: RequestArgs) {
    let result = extend({}, this.config.headers, args);

    if (!args) {
      return result;
    }

    extend(result, args.headers);

    const content = args.content,
      params = args.parameters;

    if (content && ['put', 'patch', 'delete'].indexOf(op.method) > -1) {
      const etag =
        get(content, '$etag') ||
        get(content, 'etag') ||
        (params && (params['$etag'] || params['etag']));

      if (etag) {
        result['If-Match'] = etag;
      }
    }

    const contentType = args.contentType || getContentType(op);

    if (contentType) {
      result['Content-Type'] = contentType;
    }

    if (args.language || this.acceptLanguage) {
      result['Accept-Language'] =
        `${args.language || this.acceptLanguage};q=0.9,en;q=0.5`;
    }

    return result;
  }

  request(path: string, args?: RequestArgs) {
    log('request');
    (log('path: %o', path), log('args: %o', args));

    let parameters = args ? args.parameters : undefined,
      content = args ? args.content : undefined,
      op = this.getOperation(path),
      url = this.urlFor(op, parameters, content),
      headers = this.headersFor(op, args),
      data = content;

    log('url: %o', url);
    if (!isEmpty(headers)) {
      log('headers: %o', headers);
    }

    if (data && headers && headers['Content-Type']) {
      data = formatPayload(data, headers['Content-Type']);
      log('data: %o', data);
    }

    const extras: Dictionary<any> = {};
    if (args && args.responseType) {
      extras.responseType = args.responseType;
    }

    return this.ajax({
      method: op.method as Method,
      url,
      data,
      headers,
      timeout: REQUEST_TIMEOUT_MS,
      ...extras,
    });
  }

  ajax(config: AxiosRequestConfig) {
    const language = config.headers?.['Accept-Language'];
    if (!language && this.acceptLanguage) {
      if (!config.headers) {
        config.headers = {};
      }
      config.headers['Accept-Language'] =
        `${this.acceptLanguage};q=0.9,en;q=0.5`;
    }

    return axios(config).then((response) => {
      response = enhanceResponseWithEtag(response);
      // log('response data: %o', response.data);
      return response;
    });
  }

  login(username: string, password: string): Promise<string> {
    return this.request('login_for_access_token_token_post', {
      content: {
        username,
        password,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        grant_type: 'password',
      },
    }).then((resp) => {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const access_token = resp.data.access_token as string;

      if (this.config.headers) {
        set(this.config.headers, 'Authorization', `Bearer ${access_token}`);
      }
      return access_token;
    });
  }

  valueOf<TT>(ref: any) {
    return valueAs(this.doc, ref);
  }

  valueAs<TT>(ref: any) {
    return valueAs(this.doc, ref);
  }
}
